<template>
  <StaffLayout section="Global Link Search" showHeader>
    <Loading v-if="isLoading" />
    <div v-else>
      <div class="pb-8 border-b">
        <div class="my-4">
          <!-- Use a form to handle "enter" keystroke -->
          <form v-on:submit.prevent>
            <input
              v-model.lazy="searchText"
              placeholder="Search"
              class="w-full md:w-3/4 lg:w-1/2 shadow appearance-none border rounded py-1 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
            <SearchButton
              @click="searchLinks"
              class="block ml-0 mt-2 md:mt-0 md:inline md:ml-2"
              type="submit"
            >
              Search
            </SearchButton>
          </form>
        </div>
      </div>
      <Loading v-if="isSearchLoading" />
      <div v-else>
        <div v-if="!requestError">
          <div
            v-for="linkSection in linkSections"
            :key="linkSection.linkSectionId"
            class="pt-4"
          >
            <div class="font-bold">
              {{ linkSection.portalName }}
            </div>
            <div>{{ linkSection.linkSectionSubheading }}</div>
            <div class="border my-2 text-gray-500">
              <div
                v-for="link in linkSection.links"
                :key="link.linkId"
                class="block mb-2 px-2 cursor-pointer hover:bg-blue-200 flex"
                @click="editLink(link)"
              >
                <div
                  v-if="!link.isKnownGood"
                  :title="
                    link.isWarningSuppressed
                      ? 'Detected broken link, warning suppressed'
                      : 'Detected broken link'
                  "
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="w-4 h-4 m-1 inline float-left align-middle"
                    :class="
                      link.isWarningSuppressed ? 'text-slate-400' : 'text-orange-400'
                    "
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                    />
                  </svg>
                </div>

                <div
                  v-if="link.isKnownGood && link.isWarningSuppressed"
                  title="Link is not broken, however warnings are suppressed. It is not recommended to suppress known good links."
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="w-4 h-4 m-1 inline float-left align-middle text-slate-400"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                    />
                  </svg>
                </div>
                <div>
                  <span class="block">{{ link.label }} </span>
                  <a
                    :href="link.linkUrl"
                    target="_blank"
                    class="ml-4 block text-blue-600 dark:text-blue-500 hover:underline"
                    >{{ link.linkUrl }}</a
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="pt-4">
          <ErrorDisplay :is-visible="requestError.length > 0" :message="requestError" />
        </div>
      </div>
    </div>
    <EditPopup
      entity-name="Link"
      :is-visible="isLinkPopupVisible"
      :is-edit-mode="true"
      :save-error="saveError"
      :delete-enabled="true"
      @close="isLinkPopupVisible = false"
      @save="saveLink"
      @delete="deleteLink"
    >
      <div>
        <div
          v-if="!linkResults.find((lr) => lr.linkId == selectedLink).isKnownGood"
          :title="
            linkResults.find((lr) => lr.linkId == selectedLink).isWarningSuppressed
              ? 'Detected broken link, warning suppressed'
              : 'Detected broken link'
          "
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-5 h-5 m-1 inline float-left align-middle"
            :class="
              linkResults.find((lr) => lr.linkId == selectedLink).isWarningSuppressed
                ? 'text-slate-400'
                : 'text-orange-400'
            "
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
            />
          </svg>
          <p
            :class="
              linkResults.find((lr) => lr.linkId == selectedLink).isWarningSuppressed
                ? 'text-slate-400'
                : 'text-orange-400'
            "
          >
            {{
              linkResults.find((lr) => lr.linkId == selectedLink).isWarningSuppressed
                ? "Broken link detected, warning is being suppressed"
                : "Broken link detected, please manually verify link"
            }}
          </p>
        </div>

        <div
          v-if="
            linkResults.find((lr) => lr.linkId == selectedLink).isKnownGood &&
            linkResults.find((lr) => lr.linkId == selectedLink).isWarningSuppressed
          "
          title="Link is not broken, however warnings are suppressed. It is not recommended to suppress known good links."
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-5 h-5 m-1 inline float-left align-middle text-slate-400"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
            />
          </svg>
          <p class="text-slate-400">
            Warnings for this link are being suppressed, however link has been verified by
            checker. It is recommended to now disable warning suppression for this link.
          </p>
        </div>
      </div>
      <h2 class="font-bold mt-4" v-if="linkResults">
        {{ linkResults.find((lr) => lr.linkId == selectedLink).portalName }}
      </h2>
      <h3 class="font-semibold" v-if="linkResults">
        {{ linkResults.find((lr) => lr.linkId == selectedLink).linkSectionSubheading }}
      </h3>
      <BaseInput label="Label" v-model="linkLabel" :error="errorsLink.linkLabel" />
      <BaseInput label="Address" v-model="linkUrl" :error="errorsLink.linkUrl" />
      <BaseCheckbox label="Suppress Warning" v-model="isWarningSuppressed" class="mt-2" />
      <div
        class="text-blue-600 dark:text-blue-500 hover:underline cursor-pointer mt-4"
        @click="searchSimilarLinks(linkUrl)"
      >
        Show all other portals that have this link
      </div>
    </EditPopup>
  </StaffLayout>
</template>

<script setup lang="ts">
import { useAdminStore } from "../../store/adminStore";
import { useBldgStore } from "../../store/bldgStore";
import { SaveLink, SearchLinkDTO } from "../../store/generatedFetchClient";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";

const bldgStore = useBldgStore();
const adminStore = useAdminStore();
const isLoading = ref(false);

const linkResults = ref([] as SearchLinkDTO[]);
const linkSections = computed(() => {
  var sections = [] as {
    linkSectionId: number;
    linkSectionSubheading: string;
    linkSectionSortOrder: number;
    portalName: string;
    links: SearchLinkDTO[];
  }[];
  linkResults.value.forEach((link) => {
    var sectionsMap = sections.map((section) => section.linkSectionId);
    if (sectionsMap.includes(link.linkSectionId)) {
      sections[sectionsMap.indexOf(link.linkSectionId)].links.push(link);
    } else {
      sections.push({
        linkSectionId: link.linkSectionId,
        linkSectionSubheading: link.linkSectionSubheading,
        linkSectionSortOrder: link.linkSectionSortOrder,
        portalName: link.portalName,
        links: [link],
      });
    }
  });

  return sections;
});

onMounted(loadData);
async function loadData() {
  isLoading.value = true;
  console.log("loading data");

  var params = new URLSearchParams(location.search);
  var searchParam = params.get("search");
  console.log("search param", searchParam);

  if (searchParam != null && searchParam.length > 0) {
    searchText.value = searchParam;
    searchLinks();
  }

  isLoading.value = false;
}

const isSearchLoading = ref(false);
const searchText = ref("");
const requestError = ref("");

async function searchLinks() {
  isSearchLoading.value = true;
  requestError.value = "";

  if (searchText.value.length > 0) {
    var linkResponse = await adminStore.searchLinks(searchText.value);
    if (Array.isArray(linkResponse)) linkResults.value = linkResponse as SearchLinkDTO[];
    else requestError.value = linkResponse as string;
  }

  isSearchLoading.value = false;
}

let selectedLink = ref<number>(0);
let saveError = ref("");

const { handleSubmit: handleSubmitLink, errors: errorsLink } = useForm({
  validationSchema: yup.object({
    linkLabel: yup.string().required().max(50).label("Label"),
    linkUrl: yup.string().required().max(512).label("Address"),
  }),
  initialValues: {
    linkLabel: "",
    linkUrl: "",
  },
});

let isLinkPopupVisible = ref(false);
const { value: linkLabel } = useField<string>("linkLabel");
const { value: linkUrl } = useField<string>("linkUrl");
const { value: isWarningSuppressed } = useField<boolean>("isWarningSuppressed");

function onInvalidSubmit(ctx: any) {
  saveError.value = "One or more invalid form inputs.";
  console.log("error trying to submit form", ctx);
}

let saveLink = handleSubmitLink(onValidSubmitLink, onInvalidSubmit);

async function onValidSubmitLink() {
  console.log("saving Link");
  saveError.value = "";

  var link = {
    linkId: selectedLink.value ?? 0,
    portalId: linkResults.value.find((lr) => lr.linkId == selectedLink.value).portalId,
    linkSectionId: linkResults.value.find((lr) => lr.linkId == selectedLink.value)
      .linkSectionId,
    label: linkLabel.value,
    linkUrl: linkUrl.value,
    isWarningSuppressed: isWarningSuppressed.value,
  } as SaveLink;

  let possibleError = await bldgStore.saveLink(link);
  if (possibleError) {
    saveError.value = possibleError;
    return;
  }
  isLinkPopupVisible.value = false;

  loadData();
}

function editLink(link: SearchLinkDTO) {
  linkLabel.value = link.label ?? "";
  linkUrl.value = link.linkUrl ?? "";
  isWarningSuppressed.value = link.isWarningSuppressed;
  selectedLink.value = link.linkId ?? 0;

  saveError.value = "";
  isLinkPopupVisible.value = true;
}

async function deleteLink() {
  if (selectedLink.value) {
    console.log(`Deleting Link ${selectedLink.value}`);
    saveError.value = await bldgStore.deleteLink(selectedLink.value);
    if (saveError.value) return;
  }
  isLinkPopupVisible.value = false;

  loadData();
}

function searchSimilarLinks(link: string) {
  searchText.value = link;
  isLinkPopupVisible.value = false;
  searchLinks();
}
</script>
